import { THEME } from '@Theme';
import { css } from 'aphrodite';
import { ReactNode } from 'react';
import { Field, FieldLabel } from '../../../../web/components-v2/Field';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../../web/components/HoverCard';
import { InfoIcon } from '../../../../web/components/svgs/icons/InfoIcon';
import { overviewFieldStyles } from './overviewFieldStyles';

export interface IOverviewFieldProps {
	/** Field label shown above the value. */
	label: string;
	/** The field value — text, a link, or any node. */
	children: ReactNode;
	/** Render the value in the muted (light) color, e.g. for empty / Unknown values. */
	muted?: boolean;
	/** Let the field span two grid columns (used for wide values like Email Domain & Provider). */
	spanTwo?: boolean;
	/**
	 * Extra detail shown in a hover card behind an info icon next to the label — the legacy
	 * `AccountDetailsInfoField` `info` prop (e.g. the Billing card's Renewal Info panel).
	 */
	info?: ReactNode;
	/** `danger` turns the whole cell into a light-red callout with red label and value. */
	tone?: 'default' | 'danger';
}

/**
 * A single read-mode label/value pair in an Account Overview card's flat field grid.
 * `muted` dims the value for empty/Unknown states; `spanTwo` widens the field to two
 * columns of the grid; `tone='danger'` turns the cell into a red callout.
 */
export const OverviewField = ({
	label,
	children,
	muted = false,
	spanTwo = false,
	info,
	tone = 'default',
}: IOverviewFieldProps) => {
	const isDanger = tone === 'danger';
	return (
		<Field
			styles={[
				overviewFieldStyles.field,
				...(spanTwo ? [overviewFieldStyles.spanTwo] : []),
				...(isDanger ? [overviewFieldStyles.fieldDanger] : []),
			]}
		>
			<FieldLabel styles={[overviewFieldStyles.label, ...(isDanger ? [overviewFieldStyles.labelDanger] : [])]}>
				{label}
				{!!info && (
					<HoverCard>
						<HoverCardTrigger className={css(overviewFieldStyles.infoIcon)} aria-label={`${label} details`}>
							<InfoIcon fillColor={isDanger ? THEME.colors.danger.dark : undefined} />
						</HoverCardTrigger>
						<HoverCardContent styles={[overviewFieldStyles.infoContent]}>{info}</HoverCardContent>
					</HoverCard>
				)}
			</FieldLabel>
			<span
				className={css(
					overviewFieldStyles.value,
					muted && overviewFieldStyles.valueMuted,
					isDanger && overviewFieldStyles.valueDanger
				)}
			>
				{children}
			</span>
		</Field>
	);
};
